import { lazy, Suspense, type ReactNode } from 'react';

import { DesireMode } from '@sb/messenger/auto_generated/messenger/arm_control_bot_service';
import { useDesireMode } from '@sbrc/hooks/useDesireMode';
import { useRobotIdentity } from '@sbrc/services/feathers-client/robot-identity';

import FullPageLoader from './FullPageLoader';

// The assembly-tools page (and the visualizer tree it pulls in) lives in its
// own chunk — this guard wraps every route, but the page is only needed when
// the robot is in assembly/calibration mode.
const AssemblyToolsPage = lazy(() => import('../pages/assembly-tools'));

interface AssemblyModeGuardProps {
  children: ReactNode;
}

/**
 * Renders the assembly tools UI in place of the requested route whenever the
 * robot is in `assembly` or `calibration` operation mode and the desire submode
 * is anything other than LIVE. Without this, switching submode while on a
 * subroute (e.g. `/move`) leaves the user staring at the live-robot UI even
 * though the backend has moved out of LIVE. Calibration derives ASSEMBLY or
 * IDLE from robby-server's desire-loop state and can never reach LIVE (no
 * arm-control-bot), so it always renders the tools here.
 *
 * Other operation modes (including `assembly_simulated` and `gpu`) are
 * intentionally not handled here — they are routed at the index page level in
 * `pages/main.tsx` and we don't want to change behavior for those modes on
 * subroutes.
 */
export function AssemblyModeGuard({ children }: AssemblyModeGuardProps) {
  const robotIdentity = useRobotIdentity();
  const operationMode = robotIdentity?.robotOperationMode;
  const { desireMode } = useDesireMode();

  if (
    (operationMode === 'assembly' || operationMode === 'calibration') &&
    desireMode !== DesireMode.DESIRE_MODE_LIVE
  ) {
    return (
      <Suspense fallback={<FullPageLoader label="Loading assembly tools" />}>
        <AssemblyToolsPage />
      </Suspense>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
